/**
 * App.tsx
 *
 * Converted from TanStack Start (Vite + file-based routing + SSR) to a plain
 * single-page React component using standard webpack.
 *
 * v2 revision (this pass):
 *  - Real logo/banner artwork swapped in; old placeholder images removed.
 *    Both were resized + converted to WebP (logo: 2048px/4.3MB → 600px/16KB;
 *    banner: 3584px/8.5MB → 2400px/365KB) — same visual quality at the sizes
 *    this page actually displays them, much faster to load on mobile.
 *  - FIXED a real wheel-accuracy bug: the rotation formula assumed `rotation`
 *    always started at 0, so from the 2nd spin onward the wheel visually
 *    stopped on the WRONG segment even though the game logic used the right
 *    one. Now normalizes against the current accumulated rotation every time
 *    (see the `spin()` function below for the full explanation).
 *  - Renamed "WL" (whitelist) → "GTD" (guaranteed) spot throughout, to match
 *    the terminology used when sharing a win on X.
 *  - Wallet insert now trims + lowercases the address (EVM addresses are
 *    case-insensitive; storing lowercase makes the DB unique constraint
 *    actually prevent duplicate claims) and attempts to persist the user's
 *    session spin-count alongside it, with a safe fallback if that column
 *    doesn't exist yet on the connected Supabase project (see README).
 *  - X share text now dynamically includes how many spins it took to win.
 *  - Mobile polish: 16px input font (prevents iOS Safari auto-zoom-on-focus),
 *    intrinsic aspect-ratio on the banner (prevents layout shift while it
 *    loads), and a stacked card layout for lottery history on small screens.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

// webpack `asset/resource` rule resolves this import to the hashed filename at build time
import logoUrl from './assets/lazybears-logo.webp';

// Served from the public/ folder (webpack-dev-server serves it at "/", and it's
// copied into dist/ on production build).
const BANNER_URL = '/images/lazybears-banner.webp';
const BANNER_RATIO = 2400 / 793; // intrinsic aspect ratio — reserves space to prevent layout shift

const X_URL  = 'https://x.com/LazyBears_';
const SUPPLY = 999;

/* ── Shared animation variant ─────────────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' as const } },
};

/* ── Root export (replaces `function Index()` from routes/index.tsx) ─── */
export default function App() {
  return (
    <div className="relative min-h-screen overflow-x-hidden text-foreground">
      <JungleBackdrop />
      <Nav />
      <main>
        <Hero />
        <Stats />
        <SpinWheelSection />
        <Lottery />
        <Tokenomics />
        <Roadmap />
        <ComingSoon />
      </main>
      <Footer />
    </div>
  );
}

/* ── Background ────────────────────────────────────────────────────────── */
function JungleBackdrop() {
  const leaves = useMemo(
    () =>
      Array.from({ length: 14 }).map(() => ({
        left:    Math.random() * 100,
        dur:     18 + Math.random() * 18,
        delay:  -Math.random() * 20,
        size:    18 + Math.random() * 22,
        opacity: 0.18 + Math.random() * 0.25,
      })),
    [],
  );

  return (
    <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0 bg-cream-gradient" />
      {leaves.map((l, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{ left: `${l.left}%`, fontSize: l.size, opacity: l.opacity }}
          initial={{ y: -60, rotate: 0 }}
          animate={{ y: '110vh', rotate: 360 }}
          transition={{ duration: l.dur, repeat: Infinity, delay: l.delay, ease: 'linear' }}
        >
          🍃
        </motion.div>
      ))}
      <div className="absolute inset-x-0 bottom-0 h-[40vh] bg-[radial-gradient(ellipse_at_bottom,oklch(0.55_0.15_145/0.25),transparent_70%)]" />
    </div>
  );
}

/* ── Logo ───────────────────────────────────────────────────────────────── */
function Logo({
  className = 'h-10 w-10',
  spin  = false,
  float = false,
}: {
  className?: string;
  spin?:  boolean;
  float?: boolean;
}) {
  return (
    <img
      src={logoUrl}
      alt="LAZYBEARS logo"
      className={`${className} object-contain${spin ? ' animate-spin-slow' : ''}${float ? ' animate-float' : ''}`}
      width={600}
      height={600}
    />
  );
}

/* ── X (Twitter) icon ───────────────────────────────────────────────────── */
function XIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M18.244 2H21.5l-7.5 8.57L23 22h-6.78l-5.31-6.94L4.8 22H1.54l8.03-9.17L1 2h6.91l4.8 6.34L18.24 2zm-1.19 18h1.87L7.04 4H5.05l12 16z" />
    </svg>
  );
}

/* ── Nav ────────────────────────────────────────────────────────────────── */
function Nav() {
  const [open, setOpen] = useState(false);
  const links = ['Spin', 'Lottery', 'Token', 'Roadmap', 'Mint'];

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0,   opacity: 1 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className="fixed top-0 z-50 w-full px-3"
    >
      <div className="mx-auto mt-3 flex w-full max-w-6xl items-center justify-between gap-3 rounded-2xl glass px-3 py-2.5 sm:px-5 sm:py-3">
        <a href="#top" className="flex min-w-0 items-center gap-2 sm:gap-3">
          <Logo className="h-10 w-10 shrink-0 sm:h-11 sm:w-11" />
          <span className="truncate font-display text-base tracking-wide text-shimmer sm:text-xl">
            LAZYBEARS
          </span>
        </a>

        <div className="hidden gap-7 text-sm font-medium md:flex">
          {links.map((l) => (
            <a
              key={l}
              href={`#${l.toLowerCase()}`}
              className="text-[color:var(--bark-dark)]/80 transition hover:text-[color:var(--honey-deep)]"
            >
              {l}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <a
            href={X_URL}
            target="_blank"
            rel="noreferrer"
            aria-label="LazyBears on X"
            className="hidden h-9 w-9 items-center justify-center rounded-full border border-[color:var(--bark)]/30 text-[color:var(--bark-dark)] transition hover:bg-[color:var(--bark-dark)] hover:text-[color:var(--cream)] sm:flex"
          >
            <XIcon className="h-4 w-4" />
          </a>
          <a
            href="#mint"
            className="shrink-0 rounded-full bg-honey-gradient px-3 py-2 text-xs font-bold text-[color:var(--bark-dark)] shadow-[var(--shadow-honey)] transition hover:scale-105 sm:px-5 sm:text-sm"
          >
            Join Den
          </a>
          <button
            aria-label="Toggle menu"
            onClick={() => setOpen((o) => !o)}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[color:var(--bark)]/30 md:hidden"
          >
            <span className="text-lg">{open ? '✕' : '☰'}</span>
          </button>
        </div>
      </div>

      {open && (
        <div className="mx-auto mt-2 flex w-full max-w-6xl flex-col gap-1 rounded-2xl glass p-3 md:hidden">
          {links.map((l) => (
            <a
              key={l}
              href={`#${l.toLowerCase()}`}
              onClick={() => setOpen(false)}
              className="rounded-lg px-3 py-2 text-sm font-medium text-[color:var(--bark-dark)] hover:bg-[color:var(--honey)]/20"
            >
              {l}
            </a>
          ))}
        </div>
      )}
    </motion.nav>
  );
}

/* ── Hero ───────────────────────────────────────────────────────────────── */
function Hero() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y       = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative flex min-h-[100svh] items-center justify-center px-4 pt-28 pb-16 sm:pt-32">
      <motion.div style={{ y, opacity }} className="relative z-10 mx-auto w-full max-w-6xl text-center">
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 1, type: 'spring', stiffness: 120 }}
          className="mx-auto mb-6 w-fit"
        >
          <Logo className="h-32 w-32 drop-shadow-[var(--shadow-honey)] sm:h-40 sm:w-40" float />
        </motion.div>

        <motion.h1
          initial="hidden"
          animate="show"
          variants={fadeUp}
          className="font-display text-4xl leading-[0.95] tracking-tight sm:text-7xl md:text-8xl lg:text-9xl"
        >
          <span className="text-shimmer">LAZYBEARS</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="mx-auto mt-5 max-w-2xl text-base text-[color:var(--bark-dark)]/80 sm:text-lg md:text-xl"
        >
          {SUPPLY} hand-drawn bears napping in the jungle canopy. Mint, hibernate, and win with the{' '}
          <span className="font-bold text-[color:var(--honey-deep)]">$BEAR</span> on-chain lottery.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="mt-8 flex w-full flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap sm:gap-4"
        >
          <a
            href="#mint"
            className="w-full max-w-xs rounded-full bg-honey-gradient px-7 py-3.5 text-center font-display text-base text-[color:var(--bark-dark)] shadow-[var(--shadow-honey)] transition hover:scale-105 sm:w-auto sm:text-lg"
          >
            MINT ON OPENSEA
          </a>
          <a
            href="#lottery"
            className="w-full max-w-xs rounded-full border-2 border-[color:var(--leaf-deep)] px-7 py-3.5 text-center font-display text-base text-[color:var(--leaf-deep)] transition hover:bg-[color:var(--leaf-deep)]/10 sm:w-auto sm:text-lg"
          >
            EXPLORE LOTTERY
          </a>
        </motion.div>

        {/* Banner image. `aspect-ratio` reserves the correct box before the
            image loads, so the page doesn't jump (a real responsiveness bug
            wide banners are prone to on slower mobile connections). */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 1 }}
          className="mt-12 overflow-hidden rounded-2xl border-4 border-[color:var(--bark)]/40 bg-[color:var(--jungle)] shadow-[var(--shadow-soft)] sm:mt-16 sm:rounded-3xl"
          style={{ aspectRatio: BANNER_RATIO }}
        >
          <img
            src={BANNER_URL}
            alt="LAZYBEARS — a crew of jungle bears building the LAZYBEARS wooden sign"
            className="h-full w-full object-cover"
            width={2400}
            height={793}
          />
        </motion.div>
      </motion.div>
    </section>
  );
}

/* ── Stats ──────────────────────────────────────────────────────────────── */
function Stats() {
  const stats = [
    { label: 'Total Supply', value: String(SUPPLY), suffix: 'BEARS' },
    { label: 'Launch Date',  value: 'TBA',          suffix: '' },
    { label: 'Marketplace',  value: 'OpenSea',      suffix: '' },
    { label: 'Utility Token',value: '$BEAR',         suffix: '' },
  ];

  return (
    <section className="relative px-4 py-16 sm:py-20">
      <div className="mx-auto grid max-w-6xl grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
        {stats.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08, duration: 0.6 }}
            whileHover={{ y: -6 }}
            className="wood-card rounded-2xl p-4 text-center sm:p-6"
          >
            <div className="font-display text-2xl text-[color:var(--honey-deep)] sm:text-3xl md:text-4xl">
              {s.value}
            </div>
            {s.suffix && (
              <div className="text-[10px] tracking-wider text-[color:var(--leaf-deep)] sm:text-xs">
                {s.suffix}
              </div>
            )}
            <div className="mt-2 text-xs text-[color:var(--bark-dark)]/70 sm:text-sm">{s.label}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ── Spin Wheel ─────────────────────────────────────────────────────────── */
type Segment = {
  label: string;
  kind:  'gtd' | 'miss';
  color: string;
  text:  string;
  icon:  string;
};

const SEGMENTS: Segment[] = [
  { label: 'TRY AGAIN', kind: 'miss', color: '#3a2a1a', text: '#f4e8d0', icon: '😴' },
  { label: 'GTD SPOT',  kind: 'gtd',  color: '#f1a12a', text: '#2a1a0a', icon: '🎟️' },
  { label: 'NOTHING',   kind: 'miss', color: '#6b4a2b', text: '#f4e8d0', icon: '🍂' },
  { label: 'SO CLOSE',  kind: 'miss', color: '#2f4a2a', text: '#f4e8d0', icon: '🐾' },
  { label: 'GTD SPOT',  kind: 'gtd',  color: '#f1a12a', text: '#2a1a0a', icon: '🎟️' },
  { label: 'TRY AGAIN', kind: 'miss', color: '#3a2a1a', text: '#f4e8d0', icon: '😴' },
  { label: 'NOTHING',   kind: 'miss', color: '#6b4a2b', text: '#f4e8d0', icon: '🍂' },
  { label: 'ZZZ…',      kind: 'miss', color: '#2f4a2a', text: '#f4e8d0', icon: '💤' },
];

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

function arcPath(cx: number, cy: number, r: number, startDeg: number, endDeg: number) {
  const start = polarToCartesian(cx, cy, r, endDeg);
  const end   = polarToCartesian(cx, cy, r, startDeg);
  const large = endDeg - startDeg <= 180 ? 0 : 1;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${large} 0 ${end.x} ${end.y} Z`;
}

function SpinWheelSection() {
  const [rotation, setRotation]     = useState(0);
  const [spinning, setSpinning]     = useState(false);
  const [result,   setResult]       = useState<Segment | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [spins,    setSpins]        = useState(0);
  const [wins,     setWins]         = useState(0);
  const [celebrate, setCelebrate]   = useState(false);
  const [modalOpen, setModalOpen]   = useState(false);
  const [winSpinCount, setWinSpinCount] = useState(1);

  const seg = 360 / SEGMENTS.length;

  const spin = () => {
    if (spinning) return;
    setShowResult(false);
    setResult(null);

    const win   = Math.random() < 0.2;
    const pool  = SEGMENTS.map((s, i) => ({ s, i })).filter(({ s }) =>
      win ? s.kind === 'gtd' : s.kind === 'miss'
    );
    const picked    = pool[Math.floor(Math.random() * pool.length)];
    const segCenter = picked.i * seg + seg / 2;
    const spinsFull = 6 + Math.floor(Math.random() * 3);

    /**
     * ACCURACY FIX — read before touching this math.
     *
     * The wheel's CSS `rotate` value is CUMULATIVE across spins (it keeps
     * growing spin after spin so the disc always spins forward, never snaps
     * backwards). That means the angle we need to ADD depends on where the
     * wheel is RIGHT NOW, not on some fixed offset.
     *
     * The bug in the previous version: it computed the delta as if
     * `rotation` were always 0 (`360 - segCenter`). That's only true for the
     * very first spin. From the 2nd spin on, the leftover angle from every
     * earlier spin silently accumulated, so the wheel would stop on a
     * different segment than the one the game logic had actually picked —
     * verified by simulation: spins 2–5 landed 292°, 270°, 67°, 270° away
     * from the intended segment.
     *
     * The fix: solve for the ABSOLUTE final rotation such that
     *   (segCenter + finalRotation) mod 360 === 0
     * i.e. the chosen segment's center is exactly under the pointer (top,
     * 0°), no matter what the current `rotation` value already is.
     */
    const targetMod = (((-(rotation + segCenter)) % 360) + 360) % 360;
    const finalRot  = rotation + spinsFull * 360 + targetMod;

    setSpinning(true);
    setRotation(finalRot);

    setTimeout(() => {
      setSpinning(false);
      setResult(picked.s);
      setShowResult(true);
      setSpins((n) => {
        const total = n + 1;
        if (picked.s.kind === 'gtd') setWinSpinCount(total);
        return total;
      });
      if (picked.s.kind === 'gtd') {
        setWins((n) => n + 1);
        setCelebrate(true);
        setTimeout(() => setModalOpen(true), 900);
        setTimeout(() => setCelebrate(false), 3200);
      }
    }, 4600);
  };

  return (
    <section id="spin" className="relative overflow-hidden px-4 py-20 sm:py-28">
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        variants={fadeUp}
        className="mx-auto max-w-6xl text-center"
      >
        <span className="inline-block rounded-full bg-[color:var(--honey)]/30 px-4 py-1 text-[11px] font-bold uppercase tracking-widest text-[color:var(--honey-deep)]">
          Free · Unlimited Spins
        </span>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl">
          <span className="text-shimmer">SPIN FOR GTD</span>
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-sm text-[color:var(--bark-dark)]/75 sm:text-base">
          Give the paw a whirl. Land on <b>GTD SPOT</b> to claim a guaranteed mint spot for LAZYBEARS. Spin as many times as you like.
        </p>
      </motion.div>

      <div className="mx-auto mt-10 grid max-w-5xl items-center gap-10 md:mt-14 md:grid-cols-[1.1fr_1fr]">
        {/* Wheel */}
        <div className="relative mx-auto aspect-square w-[min(88vw,440px)]">
          <div className="absolute inset-0 rounded-full bg-honey-gradient blur-3xl opacity-40 animate-glow" />

          {/* Pointer */}
          <div className="absolute left-1/2 top-[-14px] z-30 -translate-x-1/2">
            <motion.div
              animate={spinning ? { rotate: [-6, 6, -6] } : { rotate: 0 }}
              transition={{ duration: 0.15, repeat: spinning ? Infinity : 0 }}
              className="flex flex-col items-center"
            >
              <div className="h-0 w-0 border-x-[18px] border-b-0 border-t-[32px] border-x-transparent border-t-[color:var(--bark-dark)] drop-shadow-[0_4px_6px_rgba(0,0,0,0.4)]" />
              <div className="-mt-4 h-5 w-5 rounded-full bg-[color:var(--honey-deep)] shadow-lg ring-2 ring-[color:var(--bark-dark)]" />
            </motion.div>
          </div>

          {/* Wheel disc */}
          <motion.svg
            viewBox="0 0 200 200"
            className="relative h-full w-full drop-shadow-[0_20px_60px_rgba(0,0,0,0.35)]"
            animate={{ rotate: rotation }}
            transition={{ duration: 4.5, ease: [0.17, 0.67, 0.2, 1] }}
          >
            <circle cx="100" cy="100" r="99" fill="var(--bark-dark)" />
            {SEGMENTS.map((s, i) => {
              const start    = i * seg;
              const end      = (i + 1) * seg;
              const mid      = start + seg / 2;
              const labelPos = polarToCartesian(100, 100, 62, mid);
              const iconPos  = polarToCartesian(100, 100, 82, mid);
              return (
                <g key={i}>
                  <path
                    d={arcPath(100, 100, 95, start, end)}
                    fill={s.color}
                    stroke="var(--bark-dark)"
                    strokeWidth="1"
                  />
                  <g transform={`rotate(${mid} ${labelPos.x} ${labelPos.y})`}>
                    <text
                      x={labelPos.x}
                      y={labelPos.y}
                      fill={s.text}
                      fontSize="8"
                      fontWeight="800"
                      fontFamily="Bungee, Impact, sans-serif"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{ letterSpacing: '0.5px' }}
                    >
                      {s.label}
                    </text>
                  </g>
                  <text
                    x={iconPos.x}
                    y={iconPos.y}
                    fontSize="9"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    transform={`rotate(${mid} ${iconPos.x} ${iconPos.y})`}
                  >
                    {s.icon}
                  </text>
                </g>
              );
            })}
            {/* Rim peg dots */}
            {SEGMENTS.map((_, i) => {
              const p = polarToCartesian(100, 100, 92, i * seg);
              return <circle key={`p${i}`} cx={p.x} cy={p.y} r="2" fill="#f4e8d0" />;
            })}
          </motion.svg>

          {/* Center hub */}
          <div className="absolute left-1/2 top-1/2 z-20 h-20 w-20 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full border-[6px] border-[color:var(--bark-dark)] bg-[color:var(--cream)] shadow-lg sm:h-24 sm:w-24">
            <img src={logoUrl} alt="" className="h-full w-full object-contain p-1.5" />
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col items-center gap-5 text-center md:items-start md:text-left">
          <div className="grid w-full grid-cols-2 gap-3">
            <div className="wood-card rounded-2xl p-4 text-center">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--bark-dark)]/60">Your Spins</div>
              <div className="font-display text-3xl text-[color:var(--honey-deep)]">{spins}</div>
            </div>
            <div className="wood-card rounded-2xl p-4 text-center">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--bark-dark)]/60">GTD Wins</div>
              <div className="font-display text-3xl text-[color:var(--leaf-deep)]">{wins}</div>
            </div>
          </div>

          <button
            onClick={spin}
            disabled={spinning}
            className="w-full max-w-xs rounded-full bg-honey-gradient px-8 py-4 font-display text-lg text-[color:var(--bark-dark)] shadow-[var(--shadow-honey)] transition hover:scale-105 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:scale-100 sm:text-xl"
          >
            {spinning ? 'SPINNING…' : 'SPIN THE WHEEL'}
          </button>

          {showResult && result && result.kind === 'miss' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass w-full max-w-xs rounded-2xl p-4 text-center"
            >
              <div className="text-3xl">{result.icon}</div>
              <div className="mt-1 font-display text-base text-[color:var(--bark-dark)]">{result.label}</div>
              <div className="text-xs text-[color:var(--bark-dark)]/70">The bear is still napping. Spin again!</div>
            </motion.div>
          )}

          <p className="text-xs text-[color:var(--bark-dark)]/60">
            Unlimited spins · WL spots limited · One wallet per WL claim
          </p>
        </div>
      </div>

      {celebrate  && <ConfettiOverlay />}
      {modalOpen  && <GtdWinModal spinCount={winSpinCount} onClose={() => setModalOpen(false)} />}
    </section>
  );
}

/* ── Confetti ───────────────────────────────────────────────────────────── */
function ConfettiOverlay() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 60 }).map(() => ({
        left:  Math.random() * 100,
        delay: Math.random() * 0.6,
        dur:   2 + Math.random() * 1.6,
        rot:   Math.random() * 360,
        emoji: ['🎉', '🐻', '🍯', '✨', '🎊', '🐾', '🌿'][Math.floor(Math.random() * 7)],
        size:  18 + Math.random() * 20,
      })),
    [],
  );

  return (
    <div className="pointer-events-none fixed inset-0 z-[90] overflow-hidden">
      {pieces.map((p, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{ left: `${p.left}%`, top: '-8%', fontSize: p.size }}
          initial={{ y: 0,        rotate: 0,              opacity: 1 }}
          animate={{ y: '110vh',  rotate: p.rot + 720,    opacity: [1, 1, 0] }}
          transition={{ duration: p.dur, delay: p.delay, ease: 'easeIn' }}
        >
          {p.emoji}
        </motion.div>
      ))}
    </div>
  );
}

/* ── GTD Win Modal ──────────────────────────────────────────────────────── */
function GtdWinModal({ spinCount, onClose }: { spinCount: number; onClose: () => void }) {
  const [step,        setStep]        = useState<1 | 2>(1);
  const [wallet,       setWallet]      = useState('');
  const [savedWallet,  setSavedWallet] = useState('');
  const [error,        setError]       = useState('');
  const [saving,       setSaving]      = useState(false);

  const validWallet = (w: string) => /^0x[a-fA-F0-9]{40}$/.test(w);

  const submitWallet = async () => {
    // EVM addresses are case-insensitive (mixed case is just EIP-55 checksum
    // formatting, not a different address) — normalize to lowercase so the
    // DB's unique constraint actually catches duplicate claims instead of
    // letting "0xABC…" and "0xabc…" both slip through as if different.
    const w = wallet.trim().toLowerCase();
    if (!validWallet(w)) {
      setError('Enter a valid EVM wallet (0x… 40 chars).');
      return;
    }
    setError('');
    setSaving(true);
    try {
      // Dynamic import — webpack code-splits supabase into a separate chunk
      // so it's only loaded when a user actually wins the spin wheel.
      const { supabase } = await import('./integrations/supabase/client');
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 240) : null;

      // Attempt 1: save WITH the session spin count (requires the optional
      // `spin_count` column — see README migration).
      let { error: insertError } = await supabase.from('wl_claims').insert({
        wallet_address: w,
        source:         'spin_wheel',
        user_agent:     userAgent,
        spin_count:     spinCount,
      });

      // If that failed for any reason OTHER than "wallet already claimed",
      // it may be because this Supabase project hasn't run the spin_count
      // migration yet — retry once without it so the actual wallet claim
      // (the part that matters most) still gets saved either way.
      if (insertError && insertError.code !== '23505') {
        ({ error: insertError } = await supabase.from('wl_claims').insert({
          wallet_address: w,
          source:         'spin_wheel',
          user_agent:     userAgent,
        }));
      }

      if (insertError) {
        // Unique-index violation (23505) = wallet already claimed → treat as success
        if (insertError.code === '23505') {
          setSavedWallet(w);
          setStep(2);
        } else {
          setError(insertError.message || 'Could not save wallet. Try again.');
        }
      } else {
        setSavedWallet(w);
        setStep(2);
      }
    } catch (e: unknown) {
      const err = e as Error;
      setError(err?.message || 'Network error. Try again.');
    } finally {
      setSaving(false);
    }
  };

  // Mentions BOTH the GTD win and exactly how many spins it took, as requested.
  const spinWord  = spinCount === 1 ? '1 spin' : `${spinCount} spins`;
  const shareText =
    `I played the Spin Wheel and won a GTD spot for @LazyBears_ after ${spinWord} 🐻🎟️\n\n` +
    `999 LazyBears dropping soon on OpenSea — go spin the paw and claim yours 👇\n`;
  const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(X_URL)}`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.85, y: 20 }}
        animate={{ scale: 1,    y: 0 }}
        transition={{ type: 'spring', stiffness: 220, damping: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-3xl border-2 border-[color:var(--honey)]/60 bg-[color:var(--cream)] p-6 text-center shadow-[var(--shadow-honey)] sm:p-8"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-full border border-[color:var(--bark)]/30 bg-white/70 text-[color:var(--bark-dark)] hover:bg-[color:var(--bark-dark)] hover:text-[color:var(--cream)]"
        >
          ✕
        </button>

        {step === 1 ? (
          <>
            <motion.div
              animate={{ scale: [1, 1.12, 1], rotate: [0, -6, 6, 0] }}
              transition={{ duration: 1.6, repeat: Infinity }}
              className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-honey-gradient text-4xl shadow-[var(--shadow-honey)]"
            >
              🎉
            </motion.div>
            <h3 className="mt-4 font-display text-3xl text-shimmer sm:text-4xl">YOU WON A GTD SPOT!</h3>
            <p className="mt-2 text-sm text-[color:var(--bark-dark)]/75">
              A guaranteed mint spot for LAZYBEARS is reserved after <b>{spinWord}</b>. Drop your wallet to lock it in.
            </p>

            <div className="mt-5 text-left">
              <label className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--bark-dark)]/70">
                Your Wallet Address
              </label>
              <input
                autoFocus
                value={wallet}
                onChange={(e) => setWallet(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitWallet()}
                placeholder="0x1234...abcd"
                inputMode="text"
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                /* text-base = 16px: anything smaller makes iOS Safari
                   auto-zoom the whole page when this field is focused. */
                className="mt-1 w-full rounded-xl border-2 border-[color:var(--bark)]/25 bg-white px-4 py-3 font-mono text-base text-[color:var(--bark-dark)] outline-none transition focus:border-[color:var(--honey-deep)]"
              />
              {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
            </div>

            <button
              onClick={submitWallet}
              disabled={saving}
              className="mt-5 w-full rounded-full bg-honey-gradient px-6 py-3 font-display text-base text-[color:var(--bark-dark)] shadow-[var(--shadow-honey)] transition hover:scale-[1.02] disabled:opacity-70"
            >
              {saving ? 'SAVING…' : 'NEXT →'}
            </button>
          </>
        ) : (
          <>
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-leaf-gradient text-4xl text-white shadow-[var(--shadow-glow)]"
            >
              ✅
            </motion.div>
            <h3 className="mt-4 font-display text-2xl text-[color:var(--leaf-deep)] sm:text-3xl">
              GTD SPOT LOCKED IN
            </h3>
            <p className="mt-2 break-all text-xs font-mono text-[color:var(--bark-dark)]/70">{savedWallet}</p>
            <p className="mt-3 text-sm text-[color:var(--bark-dark)]/75">
              Final step — flex it on X so the den knows you're joining the hibernation.
            </p>

            <a
              href={shareUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[color:var(--bark-dark)] px-6 py-3 font-display text-base text-[color:var(--cream)] transition hover:scale-[1.02]"
            >
              <XIcon className="h-4 w-4" /> SHARE ON X
            </a>
            <button
              onClick={onClose}
              className="mt-3 w-full rounded-full border-2 border-[color:var(--bark)]/25 px-6 py-2.5 font-display text-sm text-[color:var(--bark-dark)] transition hover:bg-[color:var(--bark)]/10"
            >
              KEEP SPINNING
            </button>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

/* ── Lottery Countdown ──────────────────────────────────────────────────── */
function Countdown({ endsAt }: { endsAt: number }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const diff = Math.max(0, endsAt - now);
  const h    = Math.floor(diff / 3_600_000);
  const m    = Math.floor((diff % 3_600_000) / 60_000);
  const s    = Math.floor((diff % 60_000) / 1000);
  const pad  = (n: number) => String(n).padStart(2, '0');
  const blocks = [
    { v: pad(h), l: 'HRS' },
    { v: pad(m), l: 'MIN' },
    { v: pad(s), l: 'SEC' },
  ];

  return (
    <div className="grid grid-cols-3 gap-2">
      {blocks.map((b) => (
        <div
          key={b.l}
          className="rounded-xl bg-[color:var(--bark-dark)]/90 px-2 py-3 text-center text-[color:var(--cream)]"
        >
          <div className="font-display text-2xl sm:text-3xl">{b.v}</div>
          <div className="mt-0.5 text-[9px] tracking-widest opacity-70">{b.l}</div>
        </div>
      ))}
    </div>
  );
}

/* ── Lottery ────────────────────────────────────────────────────────────── */
function Lottery() {
  const endsAt         = useMemo(() => Date.now() + 1000 * 60 * 60 * 31 + 1000 * 60 * 12, []);
  const ticketsSold    = 247;
  const participants   = 84;
  const winnersPerPrize = participants >= 100 ? 3 : 1;

  const prizes = [
    { tier: 'JACKPOT',      reward: '50,000 $BEAR + 0.5 ETH',  icon: '🏆' },
    { tier: 'GOLDEN PAW',   reward: '250 USDT + Rare NFT',      icon: '🐾' },
    { tier: 'HONEY POT',    reward: '100 USDC + 5,000 $BEAR',   icon: '🍯' },
    { tier: 'MYSTERY BOX',  reward: 'Sealed Surprise Drop',      icon: '🎁' },
  ];

  const history = [
    { id: 'LB-0007', date: '2 days ago', participants: 132, prize: '40,000 $BEAR + 0.4 ETH', winner: '0x9a…f21' },
    { id: 'LB-0006', date: '4 days ago', participants: 98,  prize: '200 USDT + NFT',           winner: '0x2c…81b' },
    { id: 'LB-0005', date: '6 days ago', participants: 156, prize: '30,000 $BEAR + 0.3 ETH', winner: '0xd4…3aa' },
    { id: 'LB-0004', date: '8 days ago', participants: 71,  prize: 'Mystery Box',              winner: '0x77…e09' },
  ];

  return (
    <section id="lottery" className="relative px-4 py-20 sm:py-28">
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        variants={fadeUp}
        className="mx-auto max-w-6xl text-center"
      >
        <span className="inline-block rounded-full bg-[color:var(--honey)]/30 px-4 py-1 text-[11px] font-bold uppercase tracking-widest text-[color:var(--honey-deep)]">
          On-Chain · 48h Rounds
        </span>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl">
          <span className="text-shimmer">JUNGLE JACKPOT</span>
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-sm text-[color:var(--bark-dark)]/75 sm:text-base">
          Every round runs for <b>48 hours</b>. Buy tickets with <b>50% $BEAR + 50% fee</b> in USDT, USDC or ETH.
          Win $BEAR, stablecoins, ETH, NFTs or a Mystery Box — fully on-chain.
        </p>
      </motion.div>

      {/* Rules strip */}
      <div className="mx-auto mt-10 grid max-w-6xl grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { icon: '⏱️', t: '48h Rounds',  d: 'Auto draws on-chain' },
          { icon: '🎟️', t: '50/50 Entry', d: '$BEAR + USDT/USDC/ETH' },
          { icon: '🏆', t: '3 Winners',   d: 'If 100+ players, else 1' },
          { icon: '🔗', t: 'Unique ID',   d: 'Every lottery is verifiable' },
        ].map((r, i) => (
          <motion.div
            key={r.t}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.06, duration: 0.5 }}
            className="glass rounded-2xl p-3 text-center sm:p-4"
          >
            <div className="text-2xl">{r.icon}</div>
            <div className="mt-1 font-display text-sm text-[color:var(--honey-deep)] sm:text-base">{r.t}</div>
            <div className="text-[10px] text-[color:var(--bark-dark)]/70 sm:text-xs">{r.d}</div>
          </motion.div>
        ))}
      </div>

      {/* Active round — locked */}
      <div className="mx-auto mt-10 max-w-6xl">
        <div className="relative">
          <div className="pointer-events-none grid gap-6 select-none lg:grid-cols-5 lg:gap-8">
            {/* Main card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="wood-card relative overflow-hidden rounded-3xl p-6 sm:p-8 lg:col-span-3"
            >
              <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-honey-gradient opacity-30 blur-3xl" />
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--bark-dark)]/60">Active Round</div>
                  <div className="font-display text-2xl text-[color:var(--honey-deep)] sm:text-3xl">LB-0008</div>
                </div>
                <span className="rounded-full bg-[color:var(--leaf-deep)] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[color:var(--cream)]">
                  ● LIVE
                </span>
              </div>

              <div className="mt-5">
                <div className="mb-2 text-[10px] uppercase tracking-widest text-[color:var(--bark-dark)]/60">Round ends in</div>
                <Countdown endsAt={endsAt} />
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-[color:var(--honey)]/20 p-4 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--bark-dark)]/60">Ticket Price</div>
                  <div className="mt-1 font-display text-lg text-[color:var(--bark-dark)] sm:text-xl">
                    500 $BEAR <span className="text-[color:var(--bark-dark)]/50">+</span> $5
                  </div>
                  <div className="text-[10px] text-[color:var(--bark-dark)]/60">USDT / USDC / ETH</div>
                </div>
                <div className="rounded-2xl bg-[color:var(--leaf)]/15 p-4 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--bark-dark)]/60">Winners</div>
                  <div className="mt-1 font-display text-lg text-[color:var(--leaf-deep)] sm:text-xl">
                    {winnersPerPrize} per prize
                  </div>
                  <div className="text-[10px] text-[color:var(--bark-dark)]/60">{participants}/100 to unlock 3×</div>
                </div>
              </div>

              <div className="mt-6 grid gap-2">
                {prizes.map((p) => (
                  <div
                    key={p.tier}
                    className="flex items-center gap-3 rounded-xl border border-[color:var(--bark)]/15 bg-[color:var(--cream)]/60 p-3"
                  >
                    <div className="grid h-10 w-10 place-items-center rounded-lg bg-honey-gradient text-xl">{p.icon}</div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display text-sm text-[color:var(--bark-dark)]">{p.tier}</div>
                      <div className="truncate text-[11px] text-[color:var(--bark-dark)]/60">{p.reward}</div>
                    </div>
                    <div className="rounded-full bg-[color:var(--leaf-deep)]/10 px-2 py-0.5 text-[10px] font-bold text-[color:var(--leaf-deep)]">
                      ×{winnersPerPrize}
                    </div>
                  </div>
                ))}
              </div>

              <button
                disabled
                className="mt-6 w-full rounded-full bg-leaf-gradient px-6 py-3 font-display text-sm text-[color:var(--cream)] shadow-[var(--shadow-glow)] sm:text-base"
              >
                BUY TICKET
              </button>
            </motion.div>

            {/* Side stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="grid gap-4 lg:col-span-2"
            >
              <div className="wood-card rounded-3xl p-6 text-center">
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--bark-dark)]/60">Tickets Sold</div>
                <div className="mt-2 font-display text-5xl text-[color:var(--honey-deep)] sm:text-6xl">{ticketsSold}</div>
                <div className="mt-1 text-xs text-[color:var(--bark-dark)]/60">{participants} unique participants</div>
                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-[color:var(--bark)]/15">
                  <div
                    className="h-full bg-honey-gradient"
                    style={{ width: `${Math.min(100, (participants / 100) * 100)}%` }}
                  />
                </div>
                <div className="mt-2 text-[10px] text-[color:var(--bark-dark)]/60">
                  {Math.max(0, 100 - participants)} more players to unlock <b>3 winners</b> per prize
                </div>
              </div>

              <div className="glass rounded-3xl p-5">
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--bark-dark)]/60">Reward Pool</div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {[
                    { l: '$BEAR',   v: '125,400' },
                    { l: 'USDT',    v: '640' },
                    { l: 'USDC',    v: '310' },
                    { l: 'ETH',     v: '0.92' },
                    { l: 'NFTs',    v: '4' },
                    { l: 'Mystery', v: '2' },
                  ].map((a) => (
                    <div key={a.l} className="rounded-xl bg-[color:var(--cream)]/70 p-2 text-center">
                      <div className="font-display text-base text-[color:var(--bark-dark)]">{a.v}</div>
                      <div className="text-[10px] tracking-widest text-[color:var(--bark-dark)]/60">{a.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Blur + lock overlay */}
          <div className="pointer-events-auto absolute inset-0 z-10 rounded-3xl backdrop-blur-md">
            <div className="absolute inset-0 rounded-3xl bg-[oklch(0.96_0.03_90/0.45)]" />
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 2.6, repeat: Infinity }}
                className="flex max-w-md flex-col items-center gap-3 rounded-2xl border-2 border-[color:var(--honey)]/60 bg-[oklch(0.98_0.03_85/0.95)] px-6 py-6 text-center shadow-[var(--shadow-honey)] sm:px-10 sm:py-8"
              >
                <div className="text-4xl sm:text-5xl">🔒</div>
                <div className="font-display text-xl text-shimmer sm:text-3xl">COMING SOON</div>
                <div className="text-xs text-[color:var(--bark-dark)]/75 sm:text-sm">
                  The on-chain lottery unlocks after the LAZYBEARS mint. Rounds run every <b>48 hours</b> — buy tickets with 50% $BEAR + 50% fee in USDT/USDC/ETH and win $BEAR, stablecoins, ETH, NFTs or a Mystery Box.
                </div>
                <a
                  href={X_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 rounded-full bg-honey-gradient px-5 py-2 font-display text-xs text-[color:var(--bark-dark)] shadow-[var(--shadow-honey)] transition hover:scale-105 sm:text-sm"
                >
                  NOTIFY ME ON X
                </a>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Lottery History */}
      <div className="mx-auto mt-14 max-w-6xl">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="text-center">
          <span className="inline-block rounded-full bg-[color:var(--leaf)]/20 px-4 py-1 text-[11px] font-bold uppercase tracking-widest text-[color:var(--leaf-deep)]">
            Past Rounds
          </span>
          <h3 className="mt-3 font-display text-2xl text-[color:var(--bark-dark)] sm:text-3xl md:text-4xl">
            <span className="text-shimmer">Lottery History</span>
          </h3>
        </motion.div>

        <div className="relative mt-6 overflow-hidden rounded-2xl border border-[color:var(--bark)]/15 glass">
          <div className="pointer-events-none select-none blur-[6px]">
            <div className="hidden grid-cols-12 gap-3 border-b border-[color:var(--bark)]/15 px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-[color:var(--bark-dark)]/60 md:grid">
              <div className="col-span-2">Round ID</div>
              <div className="col-span-2">Ended</div>
              <div className="col-span-2">Players</div>
              <div className="col-span-3">Prize</div>
              <div className="col-span-3">Winner</div>
            </div>
            {history.map((h) => (
              <div
                key={h.id}
                className="grid grid-cols-2 gap-2 border-b border-[color:var(--bark)]/10 px-4 py-3 text-sm last:border-0 md:grid-cols-12 md:gap-3 md:px-5"
              >
                <div className="col-span-2 font-display text-[color:var(--honey-deep)]">{h.id}</div>
                <div className="col-span-2 text-[color:var(--bark-dark)]/70">{h.date}</div>
                <div className="col-span-2 text-[color:var(--bark-dark)]/70">
                  {h.participants} <span className="text-[10px]">players</span>
                </div>
                <div className="col-span-2 truncate text-[color:var(--bark-dark)] md:col-span-3">{h.prize}</div>
                <div className="col-span-2 truncate font-mono text-xs text-[color:var(--leaf-deep)] md:col-span-3">{h.winner}</div>
              </div>
            ))}
          </div>

          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-[color:var(--cream)]/55 backdrop-blur-md">
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="rounded-full bg-honey-gradient px-5 py-2 text-[10px] font-bold uppercase tracking-[0.3em] text-[color:var(--bark-dark)] shadow-[var(--shadow-honey)] sm:text-xs"
            >
              🔒 Coming Soon
            </motion.div>
            <p className="max-w-sm px-4 text-center text-xs text-[color:var(--bark-dark)]/80 sm:text-sm">
              On-chain lottery history will appear here after the first round closes.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Tokenomics ─────────────────────────────────────────────────────────── */
function Tokenomics() {
  const uses = [
    { icon: '🎟',  title: 'Lottery Entries', desc: 'Spend 50% $BEAR + 50% USDT/USDC/ETH to buy tickets.' },
    { icon: '🍯',  title: 'Honey Staking',   desc: 'Stake $BEAR + your bear NFT for daily honey yield.' },
    { icon: '🌿',  title: 'Trait Reroll',    desc: 'Reroll a single trait on your LAZYBEAR.' },
    { icon: '🪵',  title: 'Den Upgrades',    desc: 'Buy in-game cosmetics & habitat items.' },
    { icon: '🐾',  title: 'Governance',      desc: 'Vote on prize pools, partners & treasury moves.' },
    { icon: '🎁',  title: 'Airdrops',        desc: 'Holders earn $BEAR daily, simply for being lazy.' },
  ];

  return (
    <section id="token" className="relative px-4 py-20 sm:py-28">
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        variants={fadeUp}
        className="mx-auto max-w-6xl text-center"
      >
        <span className="inline-block rounded-full bg-[color:var(--leaf)]/20 px-4 py-1 text-[11px] font-bold uppercase tracking-widest text-[color:var(--leaf-deep)]">
          Utility
        </span>
        <h2 className="mt-4 font-display text-4xl text-shimmer sm:text-5xl md:text-6xl">$BEAR TOKEN</h2>
        <p className="mx-auto mt-4 max-w-2xl text-sm text-[color:var(--bark-dark)]/75 sm:text-base">
          The currency of the canopy. Earned by holding, spent on everything fun.
        </p>
      </motion.div>

      <div className="mx-auto mt-12 grid max-w-6xl gap-4 sm:mt-14 sm:grid-cols-2 lg:grid-cols-3">
        {uses.map((u, i) => (
          <motion.div
            key={u.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.07, duration: 0.6 }}
            whileHover={{ y: -6 }}
            className="wood-card group relative overflow-hidden rounded-2xl p-5 sm:p-6"
          >
            <div className="text-3xl sm:text-4xl">{u.icon}</div>
            <h3 className="mt-3 font-display text-lg text-[color:var(--honey-deep)] sm:text-xl">{u.title}</h3>
            <p className="mt-2 text-sm text-[color:var(--bark-dark)]/70">{u.desc}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ── Roadmap ────────────────────────────────────────────────────────────── */
function Roadmap() {
  const items = [
    { phase: '01', title: 'Awaken the Den',    desc: 'Reveal art, lore drops, community pawprints minted free.' },
    { phase: '02', title: `Mint ${SUPPLY}`,    desc: 'Launch on OpenSea. Whitelist hibernators get first paw.' },
    { phase: '03', title: '$BEAR Genesis',     desc: 'Token live. Holders begin earning daily honey.' },
    { phase: '04', title: 'Lottery Goes Live', desc: '48h on-chain draws. First Jungle Jackpot revealed.' },
    { phase: '05', title: 'Den Expansion',     desc: 'Merch, lore game, collabs with other forest projects.' },
  ];

  return (
    <section id="roadmap" className="relative px-4 py-20 sm:py-28">
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        variants={fadeUp}
        className="mx-auto max-w-6xl text-center"
      >
        <h2 className="font-display text-4xl text-shimmer sm:text-5xl md:text-6xl">THE PATH</h2>
        <p className="mx-auto mt-4 max-w-2xl text-sm text-[color:var(--bark-dark)]/75 sm:text-base">
          A trail through the canopy — paw by paw.
        </p>
      </motion.div>

      <div className="mx-auto mt-12 max-w-3xl space-y-5 sm:mt-16">
        {items.map((it, i) => (
          <motion.div
            key={it.phase}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, delay: i * 0.05 }}
            className="wood-card grid grid-cols-[auto_minmax(0,1fr)] items-start gap-4 rounded-2xl p-4 sm:p-5"
          >
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-honey-gradient font-display text-[color:var(--bark-dark)] shadow-[var(--shadow-honey)] sm:h-14 sm:w-14">
              {it.phase}
            </div>
            <div className="min-w-0">
              <h3 className="font-display text-lg text-[color:var(--honey-deep)] sm:text-xl">{it.title}</h3>
              <p className="mt-1 text-sm text-[color:var(--bark-dark)]/75">{it.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ── Coming Soon ────────────────────────────────────────────────────────── */
function ComingSoon() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const blocks = ['??', '??', '??', '??'];
  const labels = ['DAYS', 'HOURS', 'MINS', 'SECS'];

  return (
    <section id="mint" className="relative px-4 py-24 sm:py-32">
      <div className="mx-auto max-w-5xl">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative overflow-hidden rounded-[2rem] border-2 border-[color:var(--honey)]/50 bg-[oklch(0.98_0.03_85)] p-6 text-center shadow-[var(--shadow-glow)] sm:p-12 md:p-16"
        >
          <img
            src={logoUrl}
            alt=""
            aria-hidden
            className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 opacity-10 animate-spin-slow sm:h-80 sm:w-80"
          />
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="inline-block rounded-full bg-honey-gradient px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.3em] text-[color:var(--bark-dark)] sm:px-5 sm:py-2 sm:text-xs"
          >
            Launch Date · TBA
          </motion.div>

          <h2 className="mt-5 font-display text-5xl leading-none sm:mt-6 sm:text-6xl md:text-8xl">
            <span className="text-shimmer">COMING SOON</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm text-[color:var(--bark-dark)]/75 sm:text-base">
            {SUPPLY} LAZYBEARS will drop on OpenSea. The hibernation ends soon — sharpen your claws.
          </p>

          <div className="mx-auto mt-8 grid max-w-2xl grid-cols-4 gap-2 sm:mt-10 sm:gap-3">
            {blocks.map((b, i) => (
              <motion.div
                key={i}
                animate={{ scale: tick % 2 === i % 2 ? 1.04 : 1 }}
                transition={{ duration: 0.4 }}
                className="wood-card rounded-xl py-3 sm:rounded-2xl sm:py-5"
              >
                <div className="font-display text-2xl text-[color:var(--honey-deep)] sm:text-4xl md:text-5xl">{b}</div>
                <div className="mt-1 text-[9px] tracking-widest text-[color:var(--bark-dark)]/60 sm:text-[10px]">
                  {labels[i]}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:mt-10 sm:flex-row sm:gap-4">
            <a
              href={X_URL}
              target="_blank"
              rel="noreferrer"
              className="rounded-full bg-honey-gradient px-7 py-3.5 font-display text-sm text-[color:var(--bark-dark)] shadow-[var(--shadow-honey)] transition hover:scale-105 sm:text-base"
            >
              NOTIFY ME ON X
            </a>
            <a
              href={X_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[color:var(--leaf-deep)] px-7 py-3.5 font-display text-sm text-[color:var(--leaf-deep)] transition hover:bg-[color:var(--leaf-deep)]/10 sm:text-base"
            >
              <XIcon className="h-4 w-4" /> FOLLOW @LAZYBEARS_
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── Footer ─────────────────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="relative border-t border-[color:var(--bark)]/20 px-4 py-8">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 sm:flex-row">
        <div className="flex items-center gap-3">
          <Logo className="h-9 w-9" />
          <span className="font-display text-sm text-shimmer">LAZYBEARS © 2026</span>
        </div>
        <div className="flex items-center gap-4 text-sm text-[color:var(--bark-dark)]/70">
          <a href="#" className="hover:text-[color:var(--honey-deep)]">OpenSea</a>
          <a
            href={X_URL}
            target="_blank"
            rel="noreferrer"
            aria-label="LazyBears on X"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--bark)]/30 transition hover:bg-[color:var(--bark-dark)] hover:text-[color:var(--cream)]"
          >
            <XIcon className="h-4 w-4" />
          </a>
        </div>
      </div>
    </footer>
  );
}
